// Common utility functions to reduce code duplication

/**
 * Normalize notification message for display: show dollar amounts as INR (₹).
 * Used so old notifications stored with $ display consistently in INR.
 * Only replaces $ when followed by a number (currency amount).
 * @param message - Raw notification message (may contain $ or ₹)
 * @returns Message with $ amounts replaced by ₹ for display
 */
export function formatNotificationMessageForDisplay(message: string): string {
  if (!message || typeof message !== "string") return message;
  const withTitleFallback = message.replace(
    /"" has been submitted for review/g,
    '"Untitled Post" has been submitted for review'
  );
  return withTitleFallback.replace(/\$([\d,.]+)/g, "₹$1");
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: "₹",
  USD: "$",
  EUR: "€",
};

export type FormatCurrencyOptions = {
  currency?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

/**
 * Format a monetary amount with locale-aware grouping and currency symbol.
 * INR uses en-IN (Indian lakh/crore comma placement).
 */
export function formatCurrency(amount: number, options: FormatCurrencyOptions = {}): string {
  const { currency = "INR", minimumFractionDigits = 0, maximumFractionDigits = 2 } = options;
  const value = Number.isFinite(amount) ? amount : 0;
  const sign = value < 0 ? "-" : "";
  const symbol = CURRENCY_SYMBOLS[currency] ?? "₹";
  const locale = currency === "INR" ? "en-IN" : "en-US";
  const formatted = Math.abs(value).toLocaleString(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  });
  return `${sign}${symbol}${formatted}`;
}

/**
 * Format currency in compact form for large numbers
 * Uses Indian notation (Lakhs, Crores) for better readability
 * @param amount - Amount to format
 * @param currency - Currency code (default: INR)
 * @param compactThreshold - Threshold below which to show full number (default: 100000)
 * @returns Formatted string (e.g., "₹678.97 Cr" or "₹26.12 Cr")
 */
export const formatCompactCurrency = (
  amount: number,
  currency = "INR",
  compactThreshold = 100000
): string => {
  if (currency !== "INR") {
    // For non-INR currencies, use standard compact notation
    if (Math.abs(amount) >= 1000000000) {
      return `₹${(amount / 1000000000).toFixed(2)}B`;
    }
    if (Math.abs(amount) >= 1000000) {
      return `₹${(amount / 1000000).toFixed(2)}M`;
    }
    if (Math.abs(amount) >= 1000) {
      return `₹${(amount / 1000).toFixed(2)}K`;
    }
    return `₹${amount.toLocaleString()}`;
  }

  // For INR, use Indian notation
  const absAmount = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";

  // Crores (10 million = 1 crore)
  if (absAmount >= 10000000) {
    const crores = absAmount / 10000000;
    // Always show 2 decimals for precision
    return `${sign}₹${crores.toFixed(2)} Cr`;
  }

  // Lakhs (100 thousand = 1 lakh)
  if (absAmount >= 100000) {
    const lakhs = absAmount / 100000;
    // Always show 2 decimals for precision
    return `${sign}₹${lakhs.toFixed(2)} L`;
  }

  // Below threshold, show full number with commas
  if (absAmount >= compactThreshold) {
    return `${sign}₹${absAmount.toLocaleString("en-IN")}`;
  }

  // Very small numbers, show as-is
  return `${sign}₹${absAmount.toLocaleString("en-IN")}`;
};

/**
 * Format number with Indian numbering system commas
 * Indian format: 5,00,00,000 (groups of 2 digits after first 3)
 * @param num - Number to format
 * @returns Formatted string with Indian comma placement
 */
export const formatIndianNumber = (num: number | string): string => {
  // Remove all commas and clean the input
  const numStr = typeof num === "string" ? num.replace(/,/g, "") : num.toString();

  // Handle empty or invalid input
  if (!numStr || numStr === ".") {
    return numStr;
  }

  // Split into integer and decimal parts
  const parts = numStr.split(".");
  let integerPart = parts[0] || "";
  const decimalPart = parts[1] !== undefined ? `.${parts[1]}` : "";

  // Remove leading zeros except for single zero
  integerPart = integerPart.replace(/^0+/, "") || "0";

  // Indian numbering: groups of 2 digits after first 3
  let formatted = "";
  const len = integerPart.length;

  if (len <= 3) {
    formatted = integerPart;
  } else {
    // First part: all digits except last 3
    const firstPart = integerPart.substring(0, len - 3);
    // Last 3 digits
    const lastThree = integerPart.substring(len - 3);

    // Format first part with commas every 2 digits
    let firstFormatted = "";
    for (let i = firstPart.length; i > 0; i -= 2) {
      const start = Math.max(0, i - 2);
      const segment = firstPart.substring(start, i);
      firstFormatted = segment + (firstFormatted ? `,${firstFormatted}` : "");
    }

    formatted = `${firstFormatted},${lastThree}`;
  }

  return formatted + decimalPart;
};

/**
 * Convert number to words in Indian numbering system
 * Supports Lakhs and Crores
 * @param num - Number to convert
 * @returns String representation in words (e.g., "Five cr", "Two Lakhs")
 */
export { numberToWords } from "./numberToWordsIndian";

/** Remove HTML tags from text without regex (avoids ReDoS on untrusted input). */
export function stripHtmlTags(text: string, options?: { trim?: boolean }): string {
  let result = "";
  let inTag = false;

  for (const char of text) {
    if (char === "<") {
      inTag = true;
      continue;
    }
    if (char === ">") {
      inTag = false;
      continue;
    }
    if (!inTag) {
      result += char;
    }
  }

  return options?.trim ? result.trim() : result;
}
